import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL  || '';
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Supabase client — null when env vars not set (local-mode fallback)
export const supabase = SUPABASE_URL && SUPABASE_KEY
  ? createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth:     { persistSession: true, autoRefreshToken: true },
      realtime: { params: { eventsPerSecond: 10 } },
    })
  : null;

// ─── Auth helpers ─────────────────────────────────────────────
export const auth = {
  signIn:  (email, password) => supabase?.auth.signInWithPassword({ email, password }),
  signUp:  (email, password, metadata = {}) =>
    supabase?.auth.signUp({ email, password, options: { data: metadata } }),
  signOut: () => supabase?.auth.signOut(),
  getUser: () => supabase?.auth.getUser(),
  onAuthStateChange: (cb) => supabase?.auth.onAuthStateChange(cb),
};

// ─── Generic Edge Function invoker ────────────────────────────
async function invokeEdge(name, body) {
  if (!supabase) return { ok: false, error: 'Cloud not configured' };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) return { ok: false, error: 'Not signed in' };
    const url = `${supabase.supabaseUrl}/functions/v1/${name}`;
    const res = await fetch(url, {
      method:  'POST',
      headers: {
        Authorization:  `Bearer ${token}`,
        apikey:         supabase.supabaseKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body || {}),
    });
    let payload = null;
    try { payload = await res.json(); } catch {}
    if (!res.ok) return { ok: false, error: payload?.error || `HTTP ${res.status}`, data: payload };
    return { ok: true, data: payload };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

// ─── Database helpers ─────────────────────────────────────────
export const db = {

  // ── Multi-user: company + membership ────────────────────────
  company: {
    getForUser: async (userId) => {
      if (!supabase) return { data: null };
      const { data: member } = await supabase
        .from('account_members')
        .select('company_id, role, company:companies(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      return { data: member };
    },
    get:    (id)       => supabase?.from('companies').select('*').eq('id', id).single(),
    update: (id, data) => supabase?.from('companies').update(data).eq('id', id),
    create: (data)     => supabase?.from('companies').insert(data).select().single(),
  },
  members: {
    list:   (companyId) => supabase?.from('account_members').select('*').eq('company_id', companyId),
    create: (data)      => supabase?.from('account_members').insert(data).select().single(),
    delete: (userId, companyId) =>
      supabase?.from('account_members').delete()
        .eq('user_id', userId).eq('company_id', companyId),
  },

  // ── Customers / GCs ──────────────────────────────────────────
  customers: {
    list:   (companyId) => supabase?.from('customers').select('*').eq('company_id', companyId).order('name'),
    get:    (id)        => supabase?.from('customers').select('*').eq('id', id).single(),
    create: (data)      => supabase?.from('customers').insert(data).select().single(),
    update: (id, data)  => supabase?.from('customers').update(data).eq('id', id),
    delete: (id)        => supabase?.from('customers').delete().eq('id', id),
  },

  // ── Jobs ─────────────────────────────────────────────────────
  jobs: {
    list:   (companyId) => supabase?.from('jobs').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
    get:    (id)        => supabase?.from('jobs').select('*').eq('id', id).single(),
    create: (data)      => supabase?.from('jobs').insert(data).select().single(),
    update: (id, data)  => supabase?.from('jobs').update(data).eq('id', id),
    delete: (id)        => supabase?.from('jobs').delete().eq('id', id),
  },

  // ── Assemblies ───────────────────────────────────────────────
  assemblies: {
    listForJob: (jobId) => supabase?.from('assemblies').select('*').eq('job_id', jobId).order('created_at'),
    create: (data)      => supabase?.from('assemblies').insert(data).select().single(),
    update: (id, data)  => supabase?.from('assemblies').update(data).eq('id', id),
    delete: (id)        => supabase?.from('assemblies').delete().eq('id', id),
  },

  // ── Piece Marks ──────────────────────────────────────────────
  pieceMarks: {
    listForJob: (jobId)         => supabase?.from('piece_marks').select('*').eq('job_id', jobId).order('mark_number'),
    listForAssembly: (asmId)    => supabase?.from('piece_marks').select('*').eq('assembly_id', asmId).order('mark_number'),
    get:    (id)                => supabase?.from('piece_marks').select('*').eq('id', id).single(),
    create: (data)              => supabase?.from('piece_marks').insert(data).select().single(),
    update: (id, data)          => supabase?.from('piece_marks').update(data).eq('id', id),
    delete: (id)                => supabase?.from('piece_marks').delete().eq('id', id),
  },

  // ── Fab Stage Events ─────────────────────────────────────────
  fabStageEvents: {
    listForJob: (jobId)      => supabase?.from('fab_stage_events').select('*').eq('job_id', jobId).order('created_at', { ascending: false }),
    listForMark: (markId)    => supabase?.from('fab_stage_events').select('*').eq('piece_mark_id', markId).order('created_at', { ascending: false }),
    create: (data)           => supabase?.from('fab_stage_events').insert(data).select().single(),
  },

  // ── Work Centers ─────────────────────────────────────────────
  workCenters: {
    list:   (companyId) => supabase?.from('work_centers').select('*').eq('company_id', companyId).order('name'),
    create: (data)      => supabase?.from('work_centers').insert(data).select().single(),
    update: (id, data)  => supabase?.from('work_centers').update(data).eq('id', id),
    delete: (id)        => supabase?.from('work_centers').delete().eq('id', id),
  },

  // ── Time Cards ───────────────────────────────────────────────
  timeCards: {
    list:      (companyId) => supabase?.from('time_cards').select('*').eq('company_id', companyId).order('punched_at', { ascending: false }),
    listForJob: (jobId)    => supabase?.from('time_cards').select('*').eq('job_id', jobId).order('punched_at', { ascending: false }),
    create:    (data)      => supabase?.from('time_cards').insert(data).select().single(),
    update:    (id, data)  => supabase?.from('time_cards').update(data).eq('id', id),
    upsert:    (data)      => supabase?.from('time_cards').upsert(data, { onConflict: 'id', ignoreDuplicates: false }),
  },

  // ── Bids ─────────────────────────────────────────────────────
  bids: {
    list:   (companyId) => supabase?.from('bids').select('*').eq('company_id', companyId).order('created_at', { ascending: false }),
    get:    (id)        => supabase?.from('bids').select('*').eq('id', id).single(),
    create: (data)      => supabase?.from('bids').insert(data).select().single(),
    update: (id, data)  => supabase?.from('bids').update(data).eq('id', id),
  },

  // ── Bid Line Items ───────────────────────────────────────────
  bidLineItems: {
    listForBid:     (bidId)     => supabase?.from('bid_line_items').select('*').eq('bid_id', bidId).order('sort_order'),
    listForCompany: (companyId) => supabase?.from('bid_line_items').select('*').eq('company_id', companyId).order('sort_order'),
    create: (data)               => supabase?.from('bid_line_items').insert(data).select().single(),
    update: (id, data)           => supabase?.from('bid_line_items').update(data).eq('id', id),
    delete: (id)                 => supabase?.from('bid_line_items').delete().eq('id', id),
  },

  // ── Invoices ─────────────────────────────────────────────────
  invoices: {
    list:   (companyId) => supabase?.from('invoices').select('*').eq('company_id', companyId).order('date', { ascending: false }),
    get:    (id)        => supabase?.from('invoices').select('*').eq('id', id).single(),
    create: (data)      => supabase?.from('invoices').insert(data).select().single(),
    update: (id, data)  => supabase?.from('invoices').update(data).eq('id', id),
  },

  // ── Employees ────────────────────────────────────────────────
  employees: {
    list:   (companyId) => supabase?.from('employees').select('*').eq('company_id', companyId).order('name'),
    get:    (id)        => supabase?.from('employees').select('*').eq('id', id).single(),
    create: (data)      => supabase?.from('employees').insert(data).select().single(),
    update: (id, data)  => supabase?.from('employees').update(data).eq('id', id),
    delete: (id)        => supabase?.from('employees').delete().eq('id', id),
  },

  // ── Account Plugins ──────────────────────────────────────────
  plugins: {
    get:  (companyId) => supabase?.from('account_plugins').select('*').eq('company_id', companyId),
    set:  (companyId, accountId, pluginId, enabled) =>
      supabase?.from('account_plugins').upsert(
        { company_id: companyId, account_id: accountId, plugin_id: pluginId, enabled },
      ),
  },

  // ── Messages ─────────────────────────────────────────────────
  messages: {
    listRecent: (companyId, limit = 50) =>
      supabase?.from('messages').select('*').eq('company_id', companyId)
        .order('created_at', { ascending: false }).limit(limit),
  },

  // ── Push subscriptions ───────────────────────────────────────
  pushSubscriptions: {
    upsert: (data) =>
      supabase?.from('push_subscriptions').upsert(data, { onConflict: 'account_id,endpoint' }),
  },
};

// ─── Edge Functions ───────────────────────────────────────────
export async function sendMessageViaEdge({ to, channel, subject, body, customerId }) {
  const res = await invokeEdge('send-message', { to, channel, subject, body, customer_id: customerId ?? null });
  return { ok: res.ok, message: res.data?.message, error: res.error };
}

export async function inviteEmployeeViaEdge({ email, role, appUrl }) {
  return invokeEdge('invite-tech', { email, role, app_url: appUrl });
}

export async function acceptInviteViaEdge({ token }) {
  return invokeEdge('accept-invite', { token });
}

export async function requestPhotoUploadUrl({ jobId, pieceMarkId, kind, contentType }) {
  return invokeEdge('photo-upload-url', {
    job_id: jobId, piece_mark_id: pieceMarkId, kind, content_type: contentType,
  });
}

export async function registerPushSubscription(subscription, userAgent) {
  return invokeEdge('subscribe-push', {
    subscription: subscription.toJSON ? subscription.toJSON() : subscription,
    user_agent: userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
  });
}

export async function sendPushViaEdge({ targetUserId, title, body, url, tag }) {
  return invokeEdge('send-push', { user_id: targetUserId || undefined, title, body, url, tag });
}

export async function emailExport({ to, subject, body, attachments }) {
  return invokeEdge('email-export', { to, subject, body, attachments });
}

// ─── Real-time subscriptions ──────────────────────────────────
export const realtime = {
  // Subscribe to fab stage events (live stage advances → owner dashboard updates)
  fabStageEvents: (companyId, callback) =>
    supabase
      ?.channel(`fab_stage_events:${companyId}`)
      .on('postgres_changes', {
        event:  'INSERT',
        schema: 'public',
        table:  'fab_stage_events',
        filter: `company_id=eq.${companyId}`,
      }, callback)
      .subscribe(),

  // Subscribe to time card inserts (live punch monitoring)
  timeCards: (companyId, callback) =>
    supabase
      ?.channel(`time_cards:${companyId}`)
      .on('postgres_changes', {
        event:  'INSERT',
        schema: 'public',
        table:  'time_cards',
        filter: `company_id=eq.${companyId}`,
      }, callback)
      .subscribe(),
};

// ─── Supabase Storage upload ──────────────────────────────────
export async function uploadToStorage(storageKey, blob, contentType) {
  if (!supabase) return { ok: false, error: 'Cloud not configured' };
  const { error } = await supabase.storage.from('photos').upload(storageKey, blob, {
    contentType,
    upsert: true,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}
